import { useCallback, useMemo } from 'react';
import type { ActionMeta, GroupBase, MultiValue, OptionProps, ValueContainerProps } from 'react-select';
import Select, { components as ReactSelectComponents } from 'react-select';
import type { SourceFilter } from '@trinity/chat';
import { useTheme } from '@trinity/chat-components/components/Theme';
import * as Presentation from './Presentation';

type Props = {
  readOnly?: boolean;
  options: SourceFilter.Item[];
  value: SourceFilter.Item[];
  onChange: (value: SourceFilter.Item[], meta: ActionMeta<SourceFilter.Item>) => unknown;
};

export function Source({ options, readOnly, value, onChange }: Props) {
  const theme = useTheme();

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selected = useMemo(() => value, [value]);

  const handleChange = useCallback((value: MultiValue<SourceFilter.Item>, meta: ActionMeta<SourceFilter.Item>) => {
    onChange(Array.from(value), meta);
  }, [onChange]);

  const handleNoOptionsMessage = useCallback((obj: { inputValue: string }) => {
    return obj.inputValue?.length ? `No source found` : null;
  }, []);

  return (
    <Presentation.Row>
      <Presentation.Label>Record Type</Presentation.Label>
      <Presentation.Value>
        <Select<SourceFilter.Item, true>
          styles={selectStyles}
          components={{
            Option: MenuOption,
            ValueContainer: CollapsedValueContainer,
          }}
          isDisabled={readOnly}
          theme={selectTheme}
          value={selected}
          isMulti
          options={options}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isSearchable={false}
          isClearable={false}
          placeholder={`Select sources...`}
          noOptionsMessage={handleNoOptionsMessage}
          getOptionLabel={o => o.name}
          getOptionValue={o => o.id}
          onChange={handleChange}
          menuPlacement="auto" />
      </Presentation.Value>
    </Presentation.Row>
  );
}

function CollapsedValueContainer(
  props: ValueContainerProps<SourceFilter.Item, true, GroupBase<SourceFilter.Item>>,
) {
  const { children, getValue } = props;
  const values = getValue();
  const theme = useTheme();

  // Find the hidden Input child (last child in react-select's ValueContainer)
  // It must remain mounted for accessibility even when isSearchable=false
  const inputChild = Array.isArray(children)
    ? children[children.length - 1]
    : null;

  if (values.length === 0) {
    // No selection — show placeholder + input as normal
    return (
      <ReactSelectComponents.ValueContainer {...props}>
        {children}
      </ReactSelectComponents.ValueContainer>
    );
  }

  const [first, ...rest] = values;
  const label =
    rest.length > 0
      ? `${first.name} +${rest.length} other${rest.length > 1 ? 's' : ''}`
      : first.name;

  return (
    <ReactSelectComponents.ValueContainer {...props}>
      {/* Font matches Presentation.buildSelectStyles (Published Date/Project/Tags) for consistency */}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          fontFamily: theme.fonts.regular,
        }}
      >
        {label}
      </span>
      {inputChild}
    </ReactSelectComponents.ValueContainer>
  );
}

function MenuOption(props: OptionProps<SourceFilter.Item, true, GroupBase<SourceFilter.Item>>) {
  return (
    <ReactSelectComponents.Option {...props}>
      <input
        name={props.data.id}
        type="checkbox"
        checked={props.isSelected} />
      {props.label}
    </ReactSelectComponents.Option>
  );
}